import { type ComponentType, memo } from 'react';
import { useSelector } from 'react-redux';

import { ViewTypeName } from '@air/api/types/configurableView';

import { PublicBoardActivityContainer } from '~/components/PublicBoardActivityContainer';
import { PublicBoardAssetModal } from '~/components/PublicBoardAsset/PublicBoardAssetModal';
import { PublicKanbanView } from '~/components/v2/components/shared/kanban/PublicKanbanView';
import { PUBLIC_BOARD_CHILDREN_CONTAINER } from '~/constants/testIDs';
import { useRerenderOnMount } from '~/hooks/useRerenderOnMount';
import { AnnotationProvider } from '~/providers/AnnotationProvider/AnnotationProvider';
import { usePublicAssetModalNavigation } from '~/providers/PublicAssetModalNavigationProvider';
import { type ScrollElementContextType, useScrollElementContext } from '~/providers/ScrollElementProvider';
import { currentViewTypeNameSelector } from '~/store/configViews/selectors';

/**
 * The plumbing the board shell injects into a hosting surface's items views: the board column's
 * scroller, which the gallery virtualizes and the table's selection machinery track. Everything
 * else an items view needs — its cards, its fetch scope — is the surface's own and lives inside
 * the composed component (see PublicBoardProps).
 */
export type PublicBoardItemsViewProps = {
  scrollElementRef: ScrollElementContextType['scrollElementRef'];
};

/**
 * The hosting surface's items views, composed in from above (see PublicBoardProps). This component
 * only seats them in the view-type switch and injects the scroll plumbing.
 */
export type PublicBoardContentProps = {
  GalleryView: ComponentType<PublicBoardItemsViewProps>;
  TableView: ComponentType<PublicBoardItemsViewProps>;
};

export const PublicBoardContent = memo(({ GalleryView, TableView }: PublicBoardContentProps) => {
  const { activeClipId: clipId } = usePublicAssetModalNavigation();
  const { isMounted } = useRerenderOnMount();
  const { scrollElementRef } = useScrollElementContext();
  const viewTypeName = useSelector(currentViewTypeNameSelector);

  return (
    <div className="grow" data-testid={PUBLIC_BOARD_CHILDREN_CONTAINER}>
      {viewTypeName === ViewTypeName.gallery && <GalleryView scrollElementRef={scrollElementRef} />}
      {viewTypeName === ViewTypeName.table && <TableView scrollElementRef={scrollElementRef} />}
      {viewTypeName === ViewTypeName.kanban && <PublicKanbanView />}
      {/*
        We check here if isMounted is true or not because clipId is always false on the server but is there
        on first render client side and this check prevents a rehydration issue
      */}
      {!!clipId && isMounted && (
        <AnnotationProvider>
          <PublicBoardAssetModal />
        </AnnotationProvider>
      )}
      <PublicBoardActivityContainer />
    </div>
  );
});

PublicBoardContent.displayName = 'PublicBoardContent';
